/**
 * Environment Configuration
 *
 * This module handles environment variables with support for:
 * 1. Runtime configuration (via window.__ENV__ - injected by Docker entrypoint)
 * 2. Build-time configuration (via import.meta.env - Vite)
 *
 * Priority: Runtime > Build-time > Default
 */

// Type declaration for runtime environment
declare global {
  interface Window {
    __ENV__?: {
      VITE_API_URL?: string;
      VITE_MINIO_URL?: string;
    };
  }
}

/**
 * Get environment variable with fallback chain:
 * 1. Runtime config (window.__ENV__)
 * 2. Build-time config (import.meta.env)
 * 3. Default value
 *
 * An EMPTY string is a deliberate value, not an absent one: `VITE_API_URL=""`
 * means "call the API on the origin serving this app" (see `apiRoot()` below).
 * The checks below therefore test for `undefined`, never for truthiness — a
 * `if (value)` test would silently turn that empty string into the default.
 */
const getEnvVar = (key: string, defaultValue: string): string => {
  // Check runtime config first (Docker deployment)
  if (typeof window !== 'undefined') {
    const runtimeValue = window.__ENV__?.[key as keyof typeof window.__ENV__];
    if (runtimeValue !== undefined) {
      return runtimeValue;
    }
  }

  // Check build-time config (Vite)
  const buildValue = import.meta.env[key] as string | undefined;
  if (buildValue !== undefined) {
    return buildValue;
  }

  // Return default
  return defaultValue;
};

/**
 * Application environment configuration
 */
export const env = {
  /**
   * Backend API URL as configured.
   *
   * An empty string means "same origin as the app" — the API is reached at
   * `/api` on whatever host is serving the page. That is the production and
   * staging setup (`e-bnft.org/api`, `test.e-bnft.org/api`), and it is what
   * lets `lvopilote.bnft-tchad.org` call its own API instead of another
   * domain's. Development keeps an absolute URL: the Vite proxy only helps
   * when running `pnpm dev` on the host, not inside the Docker dev container.
   *
   * Never read this value directly to build a request URL — use `apiRoot()`,
   * `getApiBaseUrl()` or `getApiOrigin()`, which resolve the empty case.
   *
   * @example "" (same origin) | "http://localhost:8000" | "https://e-bnft.org"
   */
  API_URL: getEnvVar('VITE_API_URL', 'http://localhost:8000'),

  /**
   * MinIO Storage URL
   * @example "http://localhost:9000" or "https://storage.bnft.td"
   */
  MINIO_URL: getEnvVar('VITE_MINIO_URL', 'http://localhost:9000'),

  /**
   * Check if running in development mode
   */
  isDevelopment: import.meta.env.DEV,

  /**
   * Check if running in production mode
   */
  isProduction: import.meta.env.PROD,
} as const;

/**
 * Absolute root the API is served from, with any trailing slash or `/api/v1`
 * suffix removed.
 *
 * When `VITE_API_URL` is empty, this resolves to the origin currently serving
 * the app. An ABSOLUTE URL is always returned — never a relative path — because
 * the Service Worker persists this value in IndexedDB and replays queued
 * requests from a context where a relative URL has no meaningful base.
 */
export const apiRoot = (): string => {
  const configured = env.API_URL.replace(/\/+$/, '').replace(/\/api\/v1$/, '');
  if (configured) {
    return configured;
  }
  // Same-origin mode. `window` is absent in unit tests / SSR, where no request
  // is actually issued: fall back to the development default.
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000';
};

/**
 * Get the full API base URL with /api/v1 path
 */
export const getApiBaseUrl = (): string => {
  return `${apiRoot()}/api/v1`;
};

/**
 * Get the API origin (without path).
 *
 * Used to build URLs for files served by the API (avatars, logos, photos).
 */
export const getApiOrigin = (): string => {
  const root = apiRoot();
  try {
    return new URL(root).origin;
  } catch {
    return root;
  }
};

/**
 * Get the MinIO URL for file storage
 */
export const getMinioUrl = (): string => {
  return env.MINIO_URL.replace(/\/$/, ''); // Remove trailing slash
};

export default env;
